import React, { useState } from 'react';
import { X, Lock, Zap, CreditCard, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

const PaywallModal = ({ 
  isOpen, 
  onClose, 
  featureInfo,
  onPurchaseComplete,
  sourcePage = 'unknown'  // ✨ NEW: Track which page user came from
}) => {
  const [purchasing, setPurchasing] = useState(false);
  const [selectedOption, setSelectedOption] = useState('credits');
  
  console.log('PaywallModal featureInfo:', featureInfo);
  console.log('PaywallModal sourcePage:', sourcePage);
  
  if (!isOpen || !featureInfo) return null;

  const handlePurchase = async (type) => {
    setPurchasing(true);
    
    try {
      let endpoint;
      let body = {};
      
      if (type === 'credits') {
        // ONE-TIME PURCHASE ROUTE
        endpoint = `${API_URL}/api/subscription/create-onetime-checkout`;
        body = { 
          feature_name: featureInfo.feature_display_name,
          source_page: sourcePage  // ✨ NEW: Send source page to backend
        };
      } else {
        // PREMIUM SUBSCRIPTION ROUTE
        endpoint = `${API_URL}/api/subscription/create-checkout-session`;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setPurchasing(false);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem',
    },
    modal: {
      background: 'white',
      borderRadius: '24px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    closeButton: {
      position: 'absolute',
      top: '1.5rem',
      right: '1.5rem',
      background: '#f1f5f9',
      border: 'none',
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 200ms',
      zIndex: 1,
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '3rem 2rem 2rem 2rem',
      borderRadius: '24px 24px 0 0',
      color: 'white',
      textAlign: 'center',
    },
    icon: {
      marginBottom: '1rem',
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
      fontFamily: "'Syne', sans-serif",
    },
    subtitle: {
      fontSize: '1rem',
      opacity: 0.9,
    },
    body: {
      padding: '2rem',
    },
    featureDescription: {
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '2rem',
      border: '2px solid #e2e8f0',
    },
    optionsContainer: {
      display: 'grid',
      gap: '1rem',
      marginBottom: '2rem',
    },
    optionCard: {
      border: '2px solid #e2e8f0',
      borderRadius: '16px',
      padding: '1.5rem',
      cursor: 'pointer',
      transition: 'all 200ms',
      position: 'relative',
    },
    optionCardSelected: {
      borderColor: '#667eea',
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
    },
    optionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.75rem',
    },
    optionTitle: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: '#0f172a',
      fontFamily: "'Syne', sans-serif",
    },
    optionPrice: {
      fontSize: '1.5rem',
      fontWeight: 800,
      color: '#667eea',
      fontFamily: "'Syne', sans-serif",
    },
    optionDescription: {
      fontSize: '0.95rem',
      color: '#64748b',
      lineHeight: 1.6,
    },
    badge: {
      display: 'inline-block',
      background: '#dcfce7',
      color: '#166534',
      padding: '0.25rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginTop: '0.75rem',
    },
    button: {
      width: '100%',
      padding: '1rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    primaryButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
    features: {
      background: '#f8fafc',
      borderRadius: '12px',
      padding: '1.5rem',
      marginTop: '1.5rem',
    },
    featuresTitle: {
      fontSize: '0.9rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    featuresList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.9rem',
      color: '#475569',
    },
    checkIcon: {
      color: '#10b981',
      flexShrink: 0,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={styles.overlay}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            style={styles.modal}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              style={styles.closeButton}
              onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
            >
              <X size={20} />
            </button>

            <div style={styles.header}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring' }}
              >
                <Lock size={48} style={styles.icon} />
              </motion.div>
              <h2 style={styles.title}>Unlock {featureInfo.feature_display_name}</h2>
              <p style={styles.subtitle}>Choose how you'd like to access this feature</p>
            </div>

            <div style={styles.body}>
              <div style={styles.featureDescription}>
                <p style={{ margin: 0, fontSize: '0.95rem', color: '#475569', lineHeight: 1.6 }}>
                  {featureInfo.feature_description}
                </p>
              </div>

              <div style={styles.optionsContainer}>
                {/* One-time purchase option */}
                {featureInfo.allow_one_time_purchase && (
                  <div
                    style={{
                      ...styles.optionCard,
                      ...(selectedOption === 'credits' ? styles.optionCardSelected : {}),
                    }}
                    onClick={() => setSelectedOption('credits')}
                    onMouseEnter={(e) => {
                      if (selectedOption !== 'credits') {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedOption !== 'credits') {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }
                    }}
                  >
                    <div style={styles.optionHeader}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Zap size={24} color="#667eea" />
                        <span style={styles.optionTitle}>Pay Once</span>
                      </div>
                      <span style={styles.optionPrice}>${featureInfo.one_time_price}</span>
                    </div>
                    <p style={styles.optionDescription}>
                      Get {featureInfo.credits_per_purchase} credits for {featureInfo.feature_display_name}. 
                      Perfect for occasional use.
                    </p>
                    <span style={styles.badge}>Best for trying it out</span>
                  </div>
                )}

                {/* Premium subscription option */}
                <div
                  style={{
                    ...styles.optionCard,
                    ...(selectedOption === 'premium' ? styles.optionCardSelected : {}),
                  }}
                  onClick={() => setSelectedOption('premium')}
                  onMouseEnter={(e) => {
                    if (selectedOption !== 'premium') {
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedOption !== 'premium') {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  <div style={styles.optionHeader}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Sparkles size={24} color="#667eea" />
                      <span style={styles.optionTitle}>Premium</span>
                    </div>
                    <span style={styles.optionPrice}>$15/mo</span>
                  </div>
                  <p style={styles.optionDescription}>
                    Unlimited access to all features including {featureInfo.feature_display_name}, 
                    News Schedulers, Topic Briefings, and more.
                  </p>
                  <span style={styles.badge}>Best Value - Unlimited Use</span>

                  {selectedOption === 'premium' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      style={styles.features}
                    >
                      <div style={styles.featuresTitle}>Premium Includes:</div>
                      <div style={styles.featuresList}>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Unlimited AI call transcriptions
                        </div>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Unlimited LinkedIn connection search
                        </div>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Unlimited news schedulers
                        </div>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Unlimited topic briefings
                        </div>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Priority support
                        </div>
                        <div style={styles.featureItem}>
                          <Sparkles size={16} style={styles.checkIcon} />
                          Early access to new features
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>

              <button
                onClick={() => handlePurchase(selectedOption)}
                disabled={purchasing}
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  opacity: purchasing ? 0.6 : 1,
                  cursor: purchasing ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!purchasing) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
              >
                <CreditCard size={20} />
                {purchasing ? 'Processing...' : 
                  selectedOption === 'credits' 
                    ? `Purchase ${featureInfo.credits_per_purchase} Credits - $${featureInfo.one_time_price}`
                    : 'Upgrade to Premium - $15/month'
                }
              </button>

              <div style={{
                textAlign: 'center',
                marginTop: '1rem',
                fontSize: '0.85rem',
                color: '#64748b',
              }}>
                🔒 Secure payment powered by Stripe
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaywallModal;